/**
 * @file `Courses` — namespacer for `admin.courses.*`, backing the
 *       `/courses` panel in the admin shell.
 * @module @labcraft/admin/src/client/courses
 *
 * Calls admin's own catalog routes (`/api/courses/installed*` +
 * `/api/courses/refresh`); admin writes the `installed_courses` table
 * directly via `@labcraft/postgres` and lab learns of changes via
 * `lc_courses` LISTEN/NOTIFY — no admin→lab HTTP edge.
 */

import { CoreLifecycle } from '@labcraft/core'
import {
  DeleteInstalledCourseRoute,
  GetInstalledCoursesRoute,
  PatchInstalledCourseRoute,
  PostCoursesRefreshRoute,
  PostInstalledCourseRoute,
  type InstallCourseRequest,
  type InstalledCourse,
  type UpdateInstalledCourseRequest,
} from '../shared/courses_routes.ts'
import type { HttpTransport } from './index.ts'

export class Courses extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport

  constructor(http: HttpTransport) {
    super({ id: 'courses' })
    this.http = http
  }

  async list(): Promise<InstalledCourse[]> {
    return this.http.request(GetInstalledCoursesRoute)
  }

  async install(args: InstallCourseRequest): Promise<InstalledCourse> {
    return this.http.request(PostInstalledCourseRoute, { body: args })
  }

  async update(
    courseId: string,
    patch: UpdateInstalledCourseRequest,
  ): Promise<InstalledCourse> {
    return this.http.request(PatchInstalledCourseRoute, {
      params: { courseId },
      body: patch,
    })
  }

  async uninstall(courseId: string): Promise<{ deleted: true }> {
    return this.http.request(DeleteInstalledCourseRoute, {
      params: { courseId },
    })
  }

  async refresh(): Promise<{ courseIds: string[] }> {
    return this.http.request(PostCoursesRefreshRoute)
  }
}
